import { useState } from "react";
import { toast } from "sonner";
import { track } from "../../lib/tracking";
import { Reveal } from "../Reveal";
import { useLang } from "../../context/LanguageContext";
import { Copy, Check, ArrowUpRight, Landmark, CreditCard } from "lucide-react";

const IBAN = "IT68H0503401637000000012812";
const INTESTATARIO = "TARGET COMMUNICATIONS SRL";

export function EnrollmentSection({ course, onEnroll }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(IBAN);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = IBAN;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    track("click_copia_iban", { course: course.title });
    toast.success(t("enroll.copy_toast"));
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <section data-testid="course-enrollment" id="iscrizione" className="bg-[#F4F8FF] py-20 md:py-28">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <Reveal className="max-w-2xl mb-12">
          <span className="overline">{t("enroll.overline")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0B1220] mt-3">
            {t("enroll.title")}
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* MODE A — Online */}
          <Reveal className="relative isolate overflow-hidden bg-[#1452F0] text-white rounded-sm p-8 md:p-10 flex flex-col glow-azure">
            <div className="absolute -top-20 -right-10 h-60 w-60 rounded-full bg-[#5B9DFF]/30 blur-3xl pointer-events-none" />
            <div className="relative flex items-center gap-3 text-white/85">
              <CreditCard className="h-6 w-6" strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-white/85">{t("enroll.modeA")}</span>
            </div>
            <h3 className="relative font-heading text-3xl font-light mt-4">{t("enroll.online_title")}</h3>
            <p className="relative mt-4 text-white/80 font-light leading-relaxed flex-1">
              {t("enroll.online_desc")}
            </p>
            <button
              data-testid="enroll-online-button"
              onClick={onEnroll}
              className="group relative mt-8 bg-white text-[#0B1220] hover:bg-[#0B1220] hover:text-white transition-colors rounded-sm px-7 py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2"
            >
              {t("common.enroll_online")}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>

          {/* MODE B — Bank transfer */}
          <Reveal delay={0.1} className="bg-[#E9EFFA] rounded-sm p-8 md:p-10 flex flex-col border border-[#D5DEEE]">
            <div className="flex items-center gap-3 text-[#1452F0]">
              <Landmark className="h-6 w-6" strokeWidth={1.5} />
              <span className="overline">{t("enroll.modeB")}</span>
            </div>
            <h3 className="font-heading text-3xl font-light text-[#0B1220] mt-4">{t("enroll.transfer_title")}</h3>
            <p className="mt-4 text-[#5C6678] font-light leading-relaxed">
              {t("enroll.transfer_desc")}
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <span className="overline block mb-1">{t("enroll.holder")}</span>
                <span className="text-[#0B1220] font-medium">{INTESTATARIO}</span>
              </div>
              <div>
                <span className="overline block mb-1">IBAN</span>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <span data-testid="iban-value" className="font-mono-ibn text-base md:text-lg text-[#0B1220] tracking-tight break-all">
                    {IBAN}
                  </span>
                  <button
                    data-testid="copy-iban-button"
                    onClick={copyIban}
                    className="shrink-0 inline-flex items-center gap-2 bg-[#1452F0] text-white hover:bg-[#0E3DC0] transition-colors rounded-sm px-4 py-2 text-sm font-medium shadow-[0_0_18px_rgba(20,82,240,0.4)]"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? t("enroll.copied") : t("enroll.copy")}
                  </button>
                </div>
              </div>
            </div>

            <p className="mt-6 text-sm text-[#5C6678] font-light leading-relaxed border-t border-[#D5DEEE] pt-5">
              {t("enroll.causale1")}<strong className="text-[#0B1220] font-medium">{t("enroll.causale_bold")}</strong>{t("enroll.causale2")}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
